import { sageBaseCareServiceApi as api } from './sageBaseCareServiceApi';

export const addTagTypes = ['careEvents'] as const;
const injectedRtkApi = api
    .enhanceEndpoints({
        addTagTypes,
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getCareEvent: build.query<GetCareEventApiResponse, GetCareEventApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents/${queryArg.careEventRid}`,
                    headers: { Authorization: queryArg?.authorization },
                }),
            }),
            updateCareEvent: build.mutation<UpdateCareEventApiResponse, UpdateCareEventApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents/${queryArg.careEventRid}`,
                    method: 'PUT',
                    body: queryArg.updateCareEventRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
            }),
            addCareEventNote: build.mutation<AddCareEventNoteApiResponse, AddCareEventNoteApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents/${queryArg.careEventRid}/notes`,
                    method: 'POST',
                    body: queryArg.createCareEventNoteRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['careEvents'],
            }),
            getCareEvents: build.query<GetCareEventsApiResponse, GetCareEventsApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents`,
                    headers: { Authorization: queryArg?.authorization },
                    params: {
                        facilityRid: queryArg.facilityRid,
                        residentRids: queryArg.residentRids,
                        creatorEntityRids: queryArg.creatorEntityRids,
                        careEventRids: queryArg.careEventRids,
                        withoutAlertRid: queryArg.withoutAlertRid,
                        careOutcomes: queryArg.careOutcomes,
                        careProvided: queryArg.careProvided,
                        pageNumber: queryArg.pageNumber,
                        pageSize: queryArg.pageSize,
                    },
                }),
            }),
            createCareEvent: build.mutation<CreateCareEventApiResponse, CreateCareEventApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents`,
                    method: 'POST',
                    body: queryArg.createCareEventRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
            }),
            getLatestCareEvents: build.query<
                GetLatestCareEventsApiResponse,
                GetLatestCareEventsApiArg
            >({
                query: (queryArg) => ({
                    url: `/careEvents/latest`,
                    headers: { Authorization: queryArg?.authorization },
                    params: {
                        residentRids: queryArg.residentRids,
                        staffMemberRids: queryArg.staffMemberRids,
                    },
                }),
            }),
            searchCareEvents: build.mutation<SearchCareEventsApiResponse, SearchCareEventsApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents/search`,
                    method: 'POST',
                    body: queryArg.searchCareEventsRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
            }),
            migrateTaskToCareEvent: build.mutation<
                MigrateTaskToCareEventApiResponse,
                MigrateTaskToCareEventApiArg
            >({
                query: (queryArg) => ({
                    url: `/careEvents/migrate`,
                    method: 'POST',
                    body: queryArg.body,
                    headers: { Authorization: queryArg?.authorization },
                }),
            }),
        }),
        overrideExisting: false,
    });
export { injectedRtkApi as sageCareServiceApi };
export type GetCareEventApiResponse = /** status 200 OK */ GetCareEventResponse;
export type GetCareEventApiArg = {
    authorization?: string;
    careEventRid: string;
};
export type UpdateCareEventApiResponse = /** status 200 OK */ CareEvent;
export type UpdateCareEventApiArg = {
    authorization?: string;
    careEventRid: string;
    updateCareEventRequest: UpdateCareEventRequest;
};
export type AddCareEventNoteApiResponse = /** status 200 OK */ CareEventNote;
export type AddCareEventNoteApiArg = {
    authorization?: string;
    careEventRid: string;
    createCareEventNoteRequest: CreateCareEventNoteRequest;
};
export type GetCareEventsApiResponse = /** status 200 OK */ GetCareEventsResponse;
export type GetCareEventsApiArg = {
    authorization?: string;
    facilityRid?: string;
    residentRids?: string[];
    /** The rid of the creator entity that initiated the documentation of the care event.
    This includes both alertRid and plannedCareRid values.
     */
    creatorEntityRids?: string[];
    /** Care Event RIDs to filter by.
     */
    careEventRids?: string[];
    /** When set to true, only returns care events where alertRid is not set.
    This is useful for finding logged care that are not associated with alerts.
     */
    withoutAlertRid?: boolean;
    /** Care outcomes to filter by. If absent, all care outcomes will be returned. */
    careOutcomes?: OutcomeType[];
    /** Care provided to filter by. If absent, all care provided will be returned. */
    careProvided?: CareProvided[];
    pageNumber?: number;
    pageSize?: number;
};
export type CreateCareEventApiResponse = /** status 200 OK */ {
    rid?: string;
};
export type CreateCareEventApiArg = {
    authorization?: string;
    createCareEventRequest: CreateCareEventRequest;
};
export type GetLatestCareEventsApiResponse = /** status 200 OK */ CareEvent[];
export type GetLatestCareEventsApiArg = {
    authorization?: string;
    residentRids?: string[];
    /** The rid of a staff member who responded to a care event.
    This does not have to be the creator and will use the respondingStaffRids field on the care event.
     */
    staffMemberRids?: string[];
};
export type SearchCareEventsApiResponse = /** status 200 OK */ SearchCareEventsResponse;
export type SearchCareEventsApiArg = {
    authorization?: string;
    searchCareEventsRequest: SearchCareEventsRequest;
};
export type MigrateTaskToCareEventApiResponse = /** status 200 OK */ CareEventMigrationResponse;
export type MigrateTaskToCareEventApiArg = {
    authorization?: string;
    body: {
        id?: string;
        title?: string;
        description?: string;
        status?: string;
        severity?: string;
        updates?: {
            id?: string;
            type?: string;
            creationTimestamp?: string;
            creationTimestampInMs?: string;
            description?: string;
            author?: Author;
            careProvided?: string[];
            alertOutcome?: AlertOutcome;
        }[];
        notes?: {
            id?: string;
            creationTimestamp?: string;
            lastUpdatedTimestamp?: string;
            content?: string;
            subject?: string;
            creator?: StaffMember;
            lastUpdatedBy?: StaffMember;
        }[];
        assignee?: {
            id?: string;
            firstName?: string;
            middleName?: string;
            middleInitial?: string;
            lastName?: string;
            phoneNumber?: string;
            email?: string;
            /** Address */
            address?: {
                addressLine1?: string;
                addressLine2?: string;
                city?: string;
                state?: string;
                zipcode?: string;
                country?: string;
            };
            enterprise?: Enterprise;
            facilities?: Facility[];
            onCallStates?: {
                isOnCall: boolean;
                lastModified: string;
                facilityId: string;
                companyPhoneId?: string;
                shiftInstanceId?: string;
            }[];
            staffMemberFacilityConfigs?: {
                id?: string;
                facilityId?: string;
                staffMemberId?: string;
                careZones?: CareZone[];
                jobTitle?: string;
                isAgencyStaff?: boolean;
                isCareAdmin?: boolean;
                shouldReceiveAllCareMessages?: boolean;
            }[];
            groupMemberships?: {
                groupId?: string;
                groupName?: string;
                isAdmin?: boolean;
            }[];
            isArchived?: boolean;
            isPhoneNumberVerified?: boolean;
            /** Deprecated. Use staffMemberFacilityConfigs.shouldReceiveAllCareMessages instead. */
            shouldReceiveAllCareMessages?: boolean;
            /** Deprecated. Use staffMemberFacilityConfigs.isCareAdmin instead. */
            isCareAdmin?: boolean;
            /** Deprecated. Use staffMemberFacilityConfigs.isAgencyStaff instead. */
            isAgencyStaff?: boolean;
            /** Deprecated. Use staffMemberFacilityConfigs.jobTitle instead. */
            jobTitle?: string;
        };
        facility?: {
            id?: string;
            name?: string;
            address?: Address;
            phoneNumber?: string;
            administrator?: StaffMember;
            enterprise?: Enterprise;
            logoUrl?: string;
            emergencyNotificationNumber?: string;
            requestNotificationNumber?: string;
            taskNotificationNumber?: string;
            timezone?: string;
            careZones?: {
                id?: string;
                name?: string;
                facilityId?: string;
                /** A collection of tags used to describe a Unit. These tags will all be collected and used as search parameters, to find matching Units which may have a matching tag. */
                unitTags?: {
                    floors?: string[];
                    wings?: string[];
                    careTypes?: string[];
                    buildings?: string[];
                    wards?: string[];
                    /** Array of strings representing Rids. These will be converted to RIDs on the backend. */
                    unitRids?: string[];
                };
            }[];
            /** Delivery methods in order of preference.  Will attempt to send all messages using the first delivery methods, falling back to second delivery method and so on. */
            messageDeliveryMethods?: string[];
            /** Facility status (PRE_LIVE is deprecated. Use QA instead.) */
            status?: 'QA' | 'PRE_LIVE' | 'LIVE';
            facilityConfig?: {
                isAlertLocationEnabled?: boolean;
                /** Care types for which check-in buttons are enabled at this facility. Defaults to [INDEPENDENT_LIVING] when absent.
                 */
                checkInsEnabledCareTypes?: string[];
                routineCheckIntervalInMin?: number;
                isObservationsEnabled?: boolean;
                isEventPublishingEnabled?: boolean;
                isExtendedFallVideoEnabled?: boolean;
                isDomeLightsEnabled?: boolean;
                isStaleM3AlertClosureEnabled?: boolean;
                isDetectHumanInTheLoopEnabled?: boolean;
                isDoorAlertCareConfirmationEnabled?: boolean;
                /** Mapping of CareType (string) to list of ObservationTypes */
                observationsByCareType?: {
                    [key: string]: ObservationType[];
                };
                /** Non-SNF care types for which observation details are enabled. Details are enabled for SNF care types. SNF care types are SKILLED_NURSING, SHORT_TERM_REHAB and LONG_TERM_CARE. Other care types (INDEPENDENT_LIVING, ASSISTED_LIVING, MEMORY_CARE) are considered non-SNF care types. */
                observationDetailsEnabledCareTypes?: string[];
                /** Map of EHR provider names to their configurations */
                ehrIntegrationsConfig?: {
                    [key: string]: EhrProviderConfig;
                };
                /** Detect information for the facility */
                detectConfig?: {
                    /** Whether proactive alerting is enabled at this facility. Absent means disabled. */
                    isProactiveAlertingEnabled?: boolean;
                    /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window opens. Defaults to "22:00" when absent. May be later than proactiveAlertWindowEndTime, in which case the window spans midnight. */
                    proactiveAlertWindowStartTime?: string;
                    /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window closes. Defaults to "06:00" when absent. */
                    proactiveAlertWindowEndTime?: string;
                } & {
                    detectCameras?: 'NONE' | 'ONBOARDING' | 'QA' | 'LIVE';
                    isDetectHumanInTheLoopEnabled?: boolean;
                    isExtendedFallVideoEnabled?: boolean;
                };
                /** Gen3 features configured for this facility */
                gen3Features?: {
                    /** Gen3 feature */
                    featureType?: 'WANDER';
                    isEnabled?: boolean;
                    /** Care types for which the feature is enabled. */
                    enabledForCareTypes?: string[];
                }[];
            };
            /** List of fields that are locked for editing due to EHR integration */
            ehrLockedFields?: {
                id?: string;
                ehrProvider?: EhrProvider;
                resourceType?: ResourceType;
                resourceField?: ResourceField;
                /** Timestamp when the locked field was created */
                createdAtMillis?: number;
            }[];
        };
        enterprise?: {
            id?: string;
            name?: string;
            address?: Address;
            phoneNumber?: string;
            administrator?: StaffMember;
            facilities?: Facility[];
            logoUrl?: string;
            notificationApplicationId?: string;
            enterpriseConfig?: {
                ssoStatus?: 'DISABLED' | 'PENDING' | 'ENABLED' | 'ENFORCED';
                ssoExemptDomains?: string[];
            };
        };
        resident?: {
            id: string;
            salutation?: string;
            firstName?: string;
            middleName?: string;
            middleInitial?: string;
            lastName?: string;
            preferredName?: string;
            dateOfBirth?: string;
            age?: number;
            personalPhone?: string;
            createTimestampMs?: number;
            updateTimestampMs?: number;
            email?: string;
            /** Contact of a resident */
            emergencyContact?: {
                id?: string;
                firstName?: string;
                lastName?: string;
                primaryPhone?: string;
                secondaryPhone?: string;
                email?: string;
                relationship?: string;
                isLocal?: boolean;
                preferredContactMethod?: string;
                hasPowerOfAttorney?: boolean;
                address?: Address;
                salutation?: string;
                primaryLanguage?: string;
            };
            contacts?: Contact[];
            unit?: Unit;
            primaryCarePhysician?: {
                id?: string;
                companyName?: string;
                companyDescription?: string;
                salutation?: string;
                firstName?: string;
                lastName?: string;
                officePhoneNumber?: string;
                personalPhoneNumber?: string;
                email?: string;
                officeFax?: string;
            };
            specialists?: ProfessionalContact[];
            notes?: Note[];
            facility?: Facility;
            enterprise?: Enterprise;
            isRoutineCheckEnabled?: boolean;
            /** Whether proactive (fall-risk) alerting is enabled for this resident. Defaults to false when absent. Alerts also require the facility's proactive alerting configuration to be enabled and within its alert window. */
            isProactiveAlertingEnabled?: boolean;
            isOnHold?: boolean;
            isArchived?: boolean;
            isCheckInsEnabled?: boolean;
            archiveReason?: string;
            /** Resident health information */
            healthInfo?: {
                /** Resident memory health information */
                memoryHealthInfo?: {
                    memoryLevel?: string;
                };
                /** Resident continence health information */
                continenceHealthInfo?: {
                    continenceLevel?: string;
                    productProvider?: string;
                    wearsPads?: boolean;
                    wearsPullups?: boolean;
                    wearsBriefs?: boolean;
                };
                /** Resident's activities of daily living information */
                adlHealthInfo?: {
                    safetyLevel?: string;
                    feedingLevel?: string;
                    bathingLevel?: string;
                    toiletingLevel?: string;
                    transferringLevel?: string;
                    mobilityLevel?: string;
                    dressingLevel?: string;
                    groomingLevel?: string;
                };
                resuscitationStrategy?: string;
                /** Resident hospice health information */
                hospiceHealthInfo?: {
                    hospiceStatus: string;
                };
            };
            preferredHospital?: {
                id?: string;
                name?: string;
                phoneNumber?: string;
                faxNumber?: string;
                address?: Address;
            };
            detectConsentStatus?: 'ACCEPTED' | 'DECLINED' | 'NOT_RECEIVED';
            /** Returns all expected exits for resident, sorted by active, then create datetime DESC */
            expectedExits?: {
                beginTimestampInUtc: string;
                endTimestampInUtc: string;
                isActive?: boolean;
                /** The staff member who created the expected exit */
                staffMemberRid?: string;
            }[];
        };
        /** A residential unit */
        unit?: {
            id?: string;
            currentOccupants?: Resident[];
            devices?: DeviceAllocation[];
            phoneNumber?: string;
            displayName?: string;
            facility?: Facility;
            enterprise?: Enterprise;
            floor?: string;
            wing?: string;
            careType?: string;
            building?: string;
            ward?: string;
        };
        door?: {
            rid: string;
            facilityRid: string;
            name?: string;
            doorAlarmSchedule: {
                rid?: string;
                /** If left blank, it is assumed that this a default, facility wide schedule. */
                doorRid?: string;
                facilityRid: string;
                alwaysAlert: boolean;
                neverAlert: boolean;
                /** The object represents a union set of excluded time ranges. Because it is a union set, it is possible to have overlapping time ranges, which will not affect the evaluation of the schedule. In other words, if the current time matches any of the given time ranges, then the schedule is inside it's exclusion window. */
                dailySchedules?: {
                    /** A map where the key is a day of the week, and the value is a list of time ranges. It accepts All as a wildcard. */
                    dayOfWeekTimeRangeMap?: {
                        [key: string]: TimeRangeArray;
                    };
                    dayOfMonthTimeRangeMap?: {
                        [key: string]: TimeRangeArray;
                    };
                };
                /** An inverted representation of the current schedule for the day. It represents the times that a door alarm will fire on a given day. */
                currentDayDisplaySchedules?: string;
                holidaySchedules?: {
                    [key: string]: {
                        startTime?: string;
                        endTime?: string;
                    }[];
                };
            };
            unit: Unit;
            deviceAllocation?: {
                room?: string;
                /** Deprecated. Use devices array instead. The single device field will be removed in a future version. */
                device?: Device;
                /** The type of device allocation (since complex allocations can have multiple devices) */
                allocationType?:
                    | 'BUTTON'
                    | 'BUTTON_WITH_RESET'
                    | 'CARE_CONFIRMATION_BUTTON'
                    | 'CHECKIN_MOVEMENT_SENSOR'
                    | 'DOOR'
                    | 'FALL_DETECTION_DEVICE'
                    | 'M3'
                    | 'OFFLINE_DOOR'
                    | 'ONLINE_DOOR'
                    | 'PULL_CORD'
                    | 'UNIVERSAL_TRANSMITTER'
                    | 'UNIVERSAL_TRANSMITTER_BUTTON';
                devices: Device[];
                id: string;
                door?: Door;
                unit?: Unit;
                facility?: Facility;
                enterprise?: Enterprise;
                displayName?: string;
                resident?: Resident;
                isCheckIn?: boolean;
                isSilent?: boolean;
            };
            /** The number of seconds a door can be left open before an alert is triggered. A value of 0 means there is no threshold and alerts will not be triggered for propped doors. This setting is currently only applicable to offline doors, with an access control device and a door sensor. */
            doorProppedThresholdInSeconds: number;
        };
        /** A union type object to allow for content authors to be one of resident, staffMember, device, schedule, or care event. */
        creator?: {
            resident?: Resident;
            staffMember?: StaffMember;
            device?: {
                id?: string;
                type?: string;
                connectivityStatus?: string;
                activationStatus?: string;
                hardwareId?: string;
                creationTimestamp?: string;
                notes?: Note[];
                lastPressTimestamp?: string;
            };
            schedule?: TaskSchedule;
            careEvent?: CareEvent;
        };
        group?: {
            id?: string;
            name?: string;
            members?: StaffMember[];
            numberOfMembers?: number;
            iconUrl?: string;
            facility?: Facility;
            enterprise?: Enterprise;
        };
        schedule?: {
            id?: string;
            taskTemplate?: {
                title?: string;
                description?: string;
                severity?: string;
                assignee?: StaffMember;
                group?: Group;
                resident?: Resident;
                unit?: Unit;
            };
            scheduleRecurrenceDefinition?: {
                cronScheduleExpression?: string;
                futureExecutionTimeInMs?: number;
                durationInMinutes?: number;
            };
            creator?: StaffMember;
            lastUpdatedBy?: StaffMember;
            facility?: Facility;
            enterprise?: Enterprise;
            creationTimestamp?: string;
            lastUpdateTimestamp?: string;
            isRecurring?: boolean;
            hasRunOnce?: boolean;
            lastSuccessfulExecutionTimeInMs?: number;
        };
        dueDateAndTime?: string;
        isRecurring?: boolean;
        creationTimestamp?: string;
        creationTimestampInMs?: number;
        lastUpdateTimestamp?: string;
        lastUpdateTimestampInMs?: number;
        careProvided?: string[];
        alertOutcome?: {
            rid?: string;
            /** categorizes the resolution of an alert */
            type?: 'RESIDENT_INTERACTION' | 'TEST_ALERT' | 'FALSE_ALARM';
            taskUpdateRid?: string;
            taskRid?: string;
        };
        careEvent?: CareEvent & {
            /** A map of staff member RIDs to staff member details */
            staffMembers?: {
                [key: string]: StaffMember;
            };
        };
        pccTaskFollowUp?: {
            taskRid: string;
            answers: {
                /** The PCC question text. Question enums will be defined in the front end. */
                question?: string;
                /** Answer to the PCC question. Answer enums will be defined in the front end. */
                answer?: string;
                /** Care outcome submitted along with the answer */
                careProvided?: CareProvided;
            }[];
        };
        careTasks?: {
            id: string;
            facilityId: string;
            shiftInstanceId: string;
            residentId: string;
            name?: string;
            careTaskType: 'PLANNED' | 'UNPLANNED' | 'PRN';
            careTaskCategory: CareProvided;
            responderType: 'COMMUNITY' | 'THIRD_PARTY';
            careTaskOutcomeType: 'COMPLETE_INCOMPLETE' | 'CUSTOM';
            taskAuditedId?: string;
            scheduledAtMs?: number;
            startTimeMs?: number;
            endTimeMs?: number;
            durationMs?: number;
            estimatedTimeMs?: number;
            instructions?: string;
            note?: string;
            chartedOutcome?: OutcomeType;
            chartedCustomOutcomeId?: string;
            supportedCustomOutcomes?: {
                id: string;
                name: string;
            }[];
            chartedAtMs?: number;
            wasChartedFromEhr?: boolean;
            respondingStaffMemberIds?: string[];
            chartRequirements?: {
                field: 'START_TIME' | 'END_TIME' | 'DURATION' | 'NOTE' | 'REASON';
                requirement:
                    | 'REQUIRED'
                    | 'REQUIRED_INCOMPLETE'
                    | 'REQUIRED_COMPLETE'
                    | 'OPTIONAL'
                    | 'OPTIONAL_INCOMPLETE'
                    | 'OPTIONAL_COMPLETE';
            }[];
            supportedChartingReasons?: {
                id?: string;
                label?: string;
                relatedOutcome?: OutcomeType;
                /** Id of the CareTaskSupportedCustomOutcome this reason relates to. */
                relatedCustomOutcomeId?: string;
            }[];
            chartedReasonId?: string;
            interventions?: {
                adlCategory?: CareProvided;
                note?: string;
                displayName: string;
            }[];
            /** The care item this care task was created from, if any. */
            careItemId?: string;
        }[];
        acknowledgements?: {
            staffMemberRid: string;
            acknowledgedAtUtc?: string;
            taskRid: string;
        }[];
    }[];
};
export type CareProvided =
    | 'ADL_BATHING'
    | 'ADL_DRESSING'
    | 'ADL_EATING'
    | 'ADL_ESCORT'
    | 'ADL_GROOMING'
    | 'ADL_ORAL_HYGIENE'
    | 'ADL_ROUTINE_CHECK'
    | 'ADL_TOILETING'
    | 'ADL_TRANSFER'
    | 'EMERGENCY_FALL'
    | 'EMERGENCY_OTHER'
    | 'EMERGENCY_SEEN_ON_FLOOR'
    | 'HOUSEKEEPING_BATHROOM'
    | 'HOUSEKEEPING_BED'
    | 'HOUSEKEEPING_LAUNDRY'
    | 'HOUSEKEEPING_OTHER'
    | 'HOUSEKEEPING_TRASH'
    | 'HOUSEKEEPING_UNSPECIFIED'
    | 'NON_EMERGENCY_ACCIDENTAL_PRESS'
    | 'NON_EMERGENCY_FOUND_DEVICE'
    | 'NON_EMERGENCY_ILLNESS'
    | 'NON_EMERGENCY_QUESTION'
    | 'NON_EMERGENCY_NO_CARE_GIVEN'
    | 'REQUEST_EMOTIONAL_SUPPORT'
    | 'REQUEST_MAINTENANCE'
    | 'REQUEST_MEDTECH'
    | 'REQUEST_OTHER'
    | 'REQUEST_OXYGEN'
    | 'REQUEST_REPAIR'
    | 'ADL_CLINICAL_MONITORING'
    | 'ADL_COGNITIVE_BEHAVIORAL'
    | 'ADL_HEARING_ASSISTANCE'
    | 'ADL_MOBILITY_AID'
    | 'ADL_OTHER'
    | 'ADL_SKIN_CARE'
    | 'ADL_VISION_ASSISTANCE'
    | 'IADL_ACTIVITY_ASSISTANCE'
    | 'IADL_FINANCIAL_ASSISTANCE'
    | 'IADL_MEDICATION_ASSISTANCE'
    | 'IADL_PET_ASSISTANCE'
    | 'IADL_TRANSPORTATION_ASSISTANCE'
    | 'IADL_OTHER'
    | 'SAFETY_ELOPEMENT_RISK_MANAGEMENT'
    | 'SAFETY_FALL_INTERVENTION'
    | 'SAFETY_OTHER'
    | 'ADL_DRESSING_FOOTWEAR'
    | 'ADL_DRESSING_LOWER_BODY'
    | 'ADL_DRESSING_UPPER_BODY'
    | 'ADL_MOBILITY_ROLL'
    | 'ADL_MOBILITY_SIT_TO_LYING'
    | 'ADL_MOBILITY_LYING_TO_SIT'
    | 'ADL_MOBILITY_SIT_TO_STAND'
    | 'ADL_MOBILITY_PICK_UP_OBJECT'
    | 'ADL_PERSONAL_HYGIENE'
    | 'ADL_TRANSFER_CAR'
    | 'ADL_TRANSFER_SHOWER'
    | 'ADL_TRANSFER_TO_CHAIR'
    | 'ADL_TRANSFER_TOILET'
    | 'WANDER_RETURNED_SAFELY'
    | 'WANDER_ESCORTED'
    | 'WANDER_OTHER';
export type OutcomeType = 'COMPLETED' | 'NOT_COMPLETED' | 'REFUSED';
export type AssistanceLevel =
    | 'INDEPENDENT'
    | 'SETUP_CLEANUP'
    | 'SUPERVISION_TOUCH'
    | 'MODERATE_ASSIST'
    | 'SUBSTANTIAL_ASSIST'
    | 'DEPENDENT';
export type CareOutcome = {
    careProvided: CareProvided;
    outcome: OutcomeType;
    assistanceLevel?: AssistanceLevel;
    creatorEntityRid?: string;
    alertRid?: string;
};
export type Observation = {
    /** Supported options for observation types */
    observationType:
        | 'BOWEL_MOVEMENT'
        | 'URINE'
        | 'MOOD_BEHAVIOR'
        | 'PAIN'
        | 'SKIN'
        | 'EATING_DRINKING_LESS'
        | 'OTHER'
        | 'SEEMS_DIFFERENT'
        | 'NEEDS_MORE_HELP'
        | 'SKIN_ISSUE'
        | 'ABNORMAL_BOWEL_MOVEMENT'
        | 'ABNORMAL_URINE'
        | 'MOOD_BEHAVIOR_ISSUE';
    observationValue?: string;
    observationNote?: string;
};
export type NoteType = 'CREATION_NOTE' | 'CAREGIVER_NOTE' | 'NURSE_ACKNOWLEDGEMENT';
export type CreateCareEventNoteRequest = {
    noteType: NoteType;
    content: string;
    creatorRid: string;
};
export type CareEventNote = CreateCareEventNoteRequest & {
    /** The timestamp in milliseconds when the note was created. */
    createTimestampMs?: number;
    /** Rid of the staff that updated the note */
    lastUpdatedByRid?: string;
    /** The timestamp in milliseconds when the note was last updated. */
    updateTimestampMs?: number;
};
export type CareEvent = {
    rid?: string;
    residentRid?: string;
    facilityRid?: string;
    alertRid?: string;
    plannedCareRid?: string;
    /** Rid of the "logged care" task that was migrated to a care event. This field is only used for migration purposes
    and will be deprecated after the care event migration is complete.
     */
    loggedCareRid?: string;
    /** Rid of the staff that documented the care event */
    creatorRid?: string;
    /** Rid of the staff that updated the care event */
    lastUpdatedByRid?: string;
    careOutcomes?: CareOutcome[];
    observations?: Observation[];
    notes?: CareEventNote[];
    /** The timestamp in milliseconds when the care event was created */
    createTimestampMs?: number;
    /** The timestamp in milliseconds when the care event was last updated. */
    updateTimestampMs?: number;
    /** A list of all staff rids that responded to the care event. */
    respondingStaffRids?: string[];
};
export type GetCareEventResponse = {
    careEvent: CareEvent;
};
export type UpdateCareEventRequest = {
    lastUpdatedByRid: string;
    careOutcomes?: CareOutcome[];
    observations?: Observation[];
    notes?: CareEventNote[];
};
export type GetCareEventsResponse = {
    hasNextPage: boolean;
    pageSize: number;
    pageNumber: number;
} & {
    careEvents: CareEvent[];
};
export type CreateCareEventRequest = CareEvent;
export type SearchCareEventsResponse = {
    careEvents: CareEvent[];
    /** The total number of care events that match the filter criteria. */
    totalCount: number;
};
export type ObservationType =
    | 'BOWEL_MOVEMENT'
    | 'URINE'
    | 'MOOD_BEHAVIOR'
    | 'PAIN'
    | 'SKIN'
    | 'EATING_DRINKING_LESS'
    | 'OTHER'
    | 'SEEMS_DIFFERENT'
    | 'NEEDS_MORE_HELP'
    | 'SKIN_ISSUE'
    | 'ABNORMAL_BOWEL_MOVEMENT'
    | 'ABNORMAL_URINE'
    | 'MOOD_BEHAVIOR_ISSUE';
export type SearchCareEventsRequest = {
    /** A list of outcomes to filter on. If an empty list, will search for all support outcome values. */
    outcomes?: OutcomeType[] | null;
    /** A list of care provided to filter on. If an empty list, will search for all supported care provided values. */
    careProvided?: CareProvided[] | null;
    /** A list of observation types to filter on. If an empty list, will search for all support observation types. */
    observationTypes?: ObservationType[] | null;
    /** The timestamp in milliseconds when the child entity was created. */
    createdAfterTimestampMs: number;
};
export type CareEventMigrationResponse = {
    /** A list of task rids that failed to migrate due to an error. */
    failedTaskRids?: string[];
    /** A list of task rids that were successfully processed, but no care event was created because
    the task did not have any care outcomes.
     */
    notMigratedToCareEvent?: string[];
    /** A map of task rids to care event rids that were successfully created.
    The key is the task rid and the value is the care event rid.
     */
    migratedToCareEvent?: {
        [key: string]: string;
    };
};
export type Address = {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    country?: string;
};
export type Contact = {
    id?: string;
    firstName?: string;
    lastName?: string;
    primaryPhone?: string;
    secondaryPhone?: string;
    email?: string;
    relationship?: string;
    isLocal?: boolean;
    preferredContactMethod?: string;
    hasPowerOfAttorney?: boolean;
    address?: Address;
    salutation?: string;
    primaryLanguage?: string;
};
export type EhrFeatureType = 'TASKING';
export type EhrFeature = {
    featureType?: EhrFeatureType;
    isEnabled?: boolean;
    /** Care types for which feature is enabled. If empty, feature is enabled for all care types. */
    enabledForCareTypes?: string[];
};
export type EhrProviderConfig = {
    isResidentProfileSyncEnabled?: boolean;
    /** Care types for which resident profile sync is enabled for this EHR provider. If empty, sync is enabled for all care types. */
    enabledForCareTypes?: string[];
    features?: EhrFeature[];
};
export type EhrProvider = 'ALIS' | 'AUGUST' | 'ECP' | 'PCC' | 'YARDI';
export type ResourceType = 'RESIDENT';
export type ResourceField =
    | 'RESIDENT_UNIT'
    | 'RESIDENT_CODE_STATUS'
    | 'RESIDENT_ON_HOLD_STATUS'
    | 'RESIDENT_DOB'
    | 'RESIDENT_PHONE_NUMBER'
    | 'RESIDENT_PREFERRED_NAME'
    | 'RESIDENT_FIRST_NAME'
    | 'RESIDENT_LAST_NAME'
    | 'RESIDENT_ARCHIVED_STATUS';
export type Facility = {
    id?: string;
    name?: string;
    address?: Address;
    phoneNumber?: string;
    administrator?: StaffMember;
    enterprise?: Enterprise;
    logoUrl?: string;
    emergencyNotificationNumber?: string;
    requestNotificationNumber?: string;
    taskNotificationNumber?: string;
    timezone?: string;
    careZones?: {
        id?: string;
        name?: string;
        facilityId?: string;
        /** A collection of tags used to describe a Unit. These tags will all be collected and used as search parameters, to find matching Units which may have a matching tag. */
        unitTags?: {
            floors?: string[];
            wings?: string[];
            careTypes?: string[];
            buildings?: string[];
            wards?: string[];
            /** Array of strings representing Rids. These will be converted to RIDs on the backend. */
            unitRids?: string[];
        };
    }[];
    /** Delivery methods in order of preference.  Will attempt to send all messages using the first delivery methods, falling back to second delivery method and so on. */
    messageDeliveryMethods?: string[];
    /** Facility status (PRE_LIVE is deprecated. Use QA instead.) */
    status?: 'QA' | 'PRE_LIVE' | 'LIVE';
    facilityConfig?: {
        isAlertLocationEnabled?: boolean;
        /** Care types for which check-in buttons are enabled at this facility. Defaults to [INDEPENDENT_LIVING] when absent.
         */
        checkInsEnabledCareTypes?: string[];
        routineCheckIntervalInMin?: number;
        isObservationsEnabled?: boolean;
        isEventPublishingEnabled?: boolean;
        isExtendedFallVideoEnabled?: boolean;
        isDomeLightsEnabled?: boolean;
        isStaleM3AlertClosureEnabled?: boolean;
        isDetectHumanInTheLoopEnabled?: boolean;
        isDoorAlertCareConfirmationEnabled?: boolean;
        /** Mapping of CareType (string) to list of ObservationTypes */
        observationsByCareType?: {
            [key: string]: ObservationType[];
        };
        /** Non-SNF care types for which observation details are enabled. Details are enabled for SNF care types. SNF care types are SKILLED_NURSING, SHORT_TERM_REHAB and LONG_TERM_CARE. Other care types (INDEPENDENT_LIVING, ASSISTED_LIVING, MEMORY_CARE) are considered non-SNF care types. */
        observationDetailsEnabledCareTypes?: string[];
        /** Map of EHR provider names to their configurations */
        ehrIntegrationsConfig?: {
            [key: string]: EhrProviderConfig;
        };
        /** Detect information for the facility */
        detectConfig?: {
            /** Whether proactive alerting is enabled at this facility. Absent means disabled. */
            isProactiveAlertingEnabled?: boolean;
            /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window opens. Defaults to "22:00" when absent. May be later than proactiveAlertWindowEndTime, in which case the window spans midnight. */
            proactiveAlertWindowStartTime?: string;
            /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window closes. Defaults to "06:00" when absent. */
            proactiveAlertWindowEndTime?: string;
        } & {
            detectCameras?: 'NONE' | 'ONBOARDING' | 'QA' | 'LIVE';
            isDetectHumanInTheLoopEnabled?: boolean;
            isExtendedFallVideoEnabled?: boolean;
        };
        /** Gen3 features configured for this facility */
        gen3Features?: {
            /** Gen3 feature */
            featureType?: 'WANDER';
            isEnabled?: boolean;
            /** Care types for which the feature is enabled. */
            enabledForCareTypes?: string[];
        }[];
    };
    /** List of fields that are locked for editing due to EHR integration */
    ehrLockedFields?: {
        id?: string;
        ehrProvider?: EhrProvider;
        resourceType?: ResourceType;
        resourceField?: ResourceField;
        /** Timestamp when the locked field was created */
        createdAtMillis?: number;
    }[];
};
export type Enterprise = {
    id?: string;
    name?: string;
    address?: Address;
    phoneNumber?: string;
    administrator?: StaffMember;
    facilities?: Facility[];
    logoUrl?: string;
    notificationApplicationId?: string;
    enterpriseConfig?: {
        ssoStatus?: 'DISABLED' | 'PENDING' | 'ENABLED' | 'ENFORCED';
        ssoExemptDomains?: string[];
    };
};
export type CareZone = {
    id?: string;
    name?: string;
    facilityId?: string;
    /** A collection of tags used to describe a Unit. These tags will all be collected and used as search parameters, to find matching Units which may have a matching tag. */
    unitTags?: {
        floors?: string[];
        wings?: string[];
        careTypes?: string[];
        buildings?: string[];
        wards?: string[];
        /** Array of strings representing Rids. These will be converted to RIDs on the backend. */
        unitRids?: string[];
    };
};
export type StaffMember = {
    id?: string;
    firstName?: string;
    middleName?: string;
    middleInitial?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    /** Address */
    address?: {
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        zipcode?: string;
        country?: string;
    };
    enterprise?: Enterprise;
    facilities?: Facility[];
    onCallStates?: {
        isOnCall: boolean;
        lastModified: string;
        facilityId: string;
        companyPhoneId?: string;
        shiftInstanceId?: string;
    }[];
    staffMemberFacilityConfigs?: {
        id?: string;
        facilityId?: string;
        staffMemberId?: string;
        careZones?: CareZone[];
        jobTitle?: string;
        isAgencyStaff?: boolean;
        isCareAdmin?: boolean;
        shouldReceiveAllCareMessages?: boolean;
    }[];
    groupMemberships?: {
        groupId?: string;
        groupName?: string;
        isAdmin?: boolean;
    }[];
    isArchived?: boolean;
    isPhoneNumberVerified?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.shouldReceiveAllCareMessages instead. */
    shouldReceiveAllCareMessages?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.isCareAdmin instead. */
    isCareAdmin?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.isAgencyStaff instead. */
    isAgencyStaff?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.jobTitle instead. */
    jobTitle?: string;
};
export type Note = {
    id?: string;
    creationTimestamp?: string;
    lastUpdatedTimestamp?: string;
    content?: string;
    subject?: string;
    creator?: StaffMember;
    lastUpdatedBy?: StaffMember;
};
export type Device = {
    id?: string;
    type?: string;
    connectivityStatus?: string;
    activationStatus?: string;
    hardwareId?: string;
    creationTimestamp?: string;
    notes?: Note[];
    lastPressTimestamp?: string;
};
export type TimeRangeArray = {
    startTime?: string;
    endTime?: string;
}[];
export type Door = {
    rid: string;
    facilityRid: string;
    name?: string;
    doorAlarmSchedule: {
        rid?: string;
        /** If left blank, it is assumed that this a default, facility wide schedule. */
        doorRid?: string;
        facilityRid: string;
        alwaysAlert: boolean;
        neverAlert: boolean;
        /** The object represents a union set of excluded time ranges. Because it is a union set, it is possible to have overlapping time ranges, which will not affect the evaluation of the schedule. In other words, if the current time matches any of the given time ranges, then the schedule is inside it's exclusion window. */
        dailySchedules?: {
            /** A map where the key is a day of the week, and the value is a list of time ranges. It accepts All as a wildcard. */
            dayOfWeekTimeRangeMap?: {
                [key: string]: TimeRangeArray;
            };
            dayOfMonthTimeRangeMap?: {
                [key: string]: TimeRangeArray;
            };
        };
        /** An inverted representation of the current schedule for the day. It represents the times that a door alarm will fire on a given day. */
        currentDayDisplaySchedules?: string;
        holidaySchedules?: {
            [key: string]: {
                startTime?: string;
                endTime?: string;
            }[];
        };
    };
    unit: Unit;
    deviceAllocation?: {
        room?: string;
        /** Deprecated. Use devices array instead. The single device field will be removed in a future version. */
        device?: Device;
        /** The type of device allocation (since complex allocations can have multiple devices) */
        allocationType?:
            | 'BUTTON'
            | 'BUTTON_WITH_RESET'
            | 'CARE_CONFIRMATION_BUTTON'
            | 'CHECKIN_MOVEMENT_SENSOR'
            | 'DOOR'
            | 'FALL_DETECTION_DEVICE'
            | 'M3'
            | 'OFFLINE_DOOR'
            | 'ONLINE_DOOR'
            | 'PULL_CORD'
            | 'UNIVERSAL_TRANSMITTER'
            | 'UNIVERSAL_TRANSMITTER_BUTTON';
        devices: Device[];
        id: string;
        door?: Door;
        unit?: Unit;
        facility?: Facility;
        enterprise?: Enterprise;
        displayName?: string;
        resident?: Resident;
        isCheckIn?: boolean;
        isSilent?: boolean;
    };
    /** The number of seconds a door can be left open before an alert is triggered. A value of 0 means there is no threshold and alerts will not be triggered for propped doors. This setting is currently only applicable to offline doors, with an access control device and a door sensor. */
    doorProppedThresholdInSeconds: number;
};
export type DeviceAllocation = {
    room?: string;
    /** Deprecated. Use devices array instead. The single device field will be removed in a future version. */
    device?: Device;
    /** The type of device allocation (since complex allocations can have multiple devices) */
    allocationType?:
        | 'BUTTON'
        | 'BUTTON_WITH_RESET'
        | 'CARE_CONFIRMATION_BUTTON'
        | 'CHECKIN_MOVEMENT_SENSOR'
        | 'DOOR'
        | 'FALL_DETECTION_DEVICE'
        | 'M3'
        | 'OFFLINE_DOOR'
        | 'ONLINE_DOOR'
        | 'PULL_CORD'
        | 'UNIVERSAL_TRANSMITTER'
        | 'UNIVERSAL_TRANSMITTER_BUTTON';
    devices: Device[];
    id: string;
    door?: Door;
    unit?: Unit;
    facility?: Facility;
    enterprise?: Enterprise;
    displayName?: string;
    resident?: Resident;
    isCheckIn?: boolean;
    isSilent?: boolean;
};
export type Unit = {
    id?: string;
    currentOccupants?: Resident[];
    devices?: DeviceAllocation[];
    phoneNumber?: string;
    displayName?: string;
    facility?: Facility;
    enterprise?: Enterprise;
    floor?: string;
    wing?: string;
    careType?: string;
    building?: string;
    ward?: string;
};
export type ProfessionalContact = {
    id?: string;
    companyName?: string;
    companyDescription?: string;
    salutation?: string;
    firstName?: string;
    lastName?: string;
    officePhoneNumber?: string;
    personalPhoneNumber?: string;
    email?: string;
    officeFax?: string;
};
export type Resident = {
    id: string;
    salutation?: string;
    firstName?: string;
    middleName?: string;
    middleInitial?: string;
    lastName?: string;
    preferredName?: string;
    dateOfBirth?: string;
    age?: number;
    personalPhone?: string;
    createTimestampMs?: number;
    updateTimestampMs?: number;
    email?: string;
    /** Contact of a resident */
    emergencyContact?: {
        id?: string;
        firstName?: string;
        lastName?: string;
        primaryPhone?: string;
        secondaryPhone?: string;
        email?: string;
        relationship?: string;
        isLocal?: boolean;
        preferredContactMethod?: string;
        hasPowerOfAttorney?: boolean;
        address?: Address;
        salutation?: string;
        primaryLanguage?: string;
    };
    contacts?: Contact[];
    unit?: Unit;
    primaryCarePhysician?: {
        id?: string;
        companyName?: string;
        companyDescription?: string;
        salutation?: string;
        firstName?: string;
        lastName?: string;
        officePhoneNumber?: string;
        personalPhoneNumber?: string;
        email?: string;
        officeFax?: string;
    };
    specialists?: ProfessionalContact[];
    notes?: Note[];
    facility?: Facility;
    enterprise?: Enterprise;
    isRoutineCheckEnabled?: boolean;
    /** Whether proactive (fall-risk) alerting is enabled for this resident. Defaults to false when absent. Alerts also require the facility's proactive alerting configuration to be enabled and within its alert window. */
    isProactiveAlertingEnabled?: boolean;
    isOnHold?: boolean;
    isArchived?: boolean;
    isCheckInsEnabled?: boolean;
    archiveReason?: string;
    /** Resident health information */
    healthInfo?: {
        /** Resident memory health information */
        memoryHealthInfo?: {
            memoryLevel?: string;
        };
        /** Resident continence health information */
        continenceHealthInfo?: {
            continenceLevel?: string;
            productProvider?: string;
            wearsPads?: boolean;
            wearsPullups?: boolean;
            wearsBriefs?: boolean;
        };
        /** Resident's activities of daily living information */
        adlHealthInfo?: {
            safetyLevel?: string;
            feedingLevel?: string;
            bathingLevel?: string;
            toiletingLevel?: string;
            transferringLevel?: string;
            mobilityLevel?: string;
            dressingLevel?: string;
            groomingLevel?: string;
        };
        resuscitationStrategy?: string;
        /** Resident hospice health information */
        hospiceHealthInfo?: {
            hospiceStatus: string;
        };
    };
    preferredHospital?: {
        id?: string;
        name?: string;
        phoneNumber?: string;
        faxNumber?: string;
        address?: Address;
    };
    detectConsentStatus?: 'ACCEPTED' | 'DECLINED' | 'NOT_RECEIVED';
    /** Returns all expected exits for resident, sorted by active, then create datetime DESC */
    expectedExits?: {
        beginTimestampInUtc: string;
        endTimestampInUtc: string;
        isActive?: boolean;
        /** The staff member who created the expected exit */
        staffMemberRid?: string;
    }[];
};
export type Group = {
    id?: string;
    name?: string;
    members?: StaffMember[];
    numberOfMembers?: number;
    iconUrl?: string;
    facility?: Facility;
    enterprise?: Enterprise;
};
export type TaskSchedule = {
    id?: string;
    taskTemplate?: {
        title?: string;
        description?: string;
        severity?: string;
        assignee?: StaffMember;
        group?: Group;
        resident?: Resident;
        unit?: Unit;
    };
    scheduleRecurrenceDefinition?: {
        cronScheduleExpression?: string;
        futureExecutionTimeInMs?: number;
        durationInMinutes?: number;
    };
    creator?: StaffMember;
    lastUpdatedBy?: StaffMember;
    facility?: Facility;
    enterprise?: Enterprise;
    creationTimestamp?: string;
    lastUpdateTimestamp?: string;
    isRecurring?: boolean;
    hasRunOnce?: boolean;
    lastSuccessfulExecutionTimeInMs?: number;
};
export type Author = {
    resident?: Resident;
    staffMember?: StaffMember;
    device?: {
        id?: string;
        type?: string;
        connectivityStatus?: string;
        activationStatus?: string;
        hardwareId?: string;
        creationTimestamp?: string;
        notes?: Note[];
        lastPressTimestamp?: string;
    };
    schedule?: TaskSchedule;
    careEvent?: CareEvent;
};
export type AlertOutcome = {
    rid?: string;
    /** categorizes the resolution of an alert */
    type?: 'RESIDENT_INTERACTION' | 'TEST_ALERT' | 'FALSE_ALARM';
    taskUpdateRid?: string;
    taskRid?: string;
};
export const {
    useGetCareEventQuery,
    useLazyGetCareEventQuery,
    useUpdateCareEventMutation,
    useAddCareEventNoteMutation,
    useGetCareEventsQuery,
    useLazyGetCareEventsQuery,
    useCreateCareEventMutation,
    useGetLatestCareEventsQuery,
    useLazyGetLatestCareEventsQuery,
    useSearchCareEventsMutation,
    useMigrateTaskToCareEventMutation,
} = injectedRtkApi;
